import { useCallback, useState } from "react";
import { klaviyoSubscribe, isValidEmail, type KlaviyoSubscribeOptions } from "@/lib/klaviyo";

export type KlaviyoStatus = "idle" | "loading" | "success" | "error";

const SUCCESS_MESSAGE = "Thank you — you’re now on the MOTAI Private List.";
const INVALID_MESSAGE = "Please enter a valid email address.";
const ERROR_MESSAGE = "Something went wrong. Please try again.";

export interface UseKlaviyoSubscribeOptions
  extends Omit<KlaviyoSubscribeOptions, "email"> {}

export function useKlaviyoSubscribe(options: UseKlaviyoSubscribeOptions = {}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<KlaviyoStatus>("idle");
  const [message, setMessage] = useState<string>("");

  const submit = useCallback(
    async (e?: React.FormEvent | React.MouseEvent) => {
      e?.preventDefault?.();
      if (status === "loading") return;

      if (!isValidEmail(email)) {
        setStatus("error");
        setMessage(INVALID_MESSAGE);
        return;
      }

      setStatus("loading");
      setMessage("");
      try {
        await klaviyoSubscribe({ email, ...options });
        setStatus("success");
        setMessage(SUCCESS_MESSAGE);
        setEmail("");
      } catch (err) {
        const code = err instanceof Error ? err.message : "";
        setStatus("error");
        setMessage(code === "invalid_email" ? INVALID_MESSAGE : ERROR_MESSAGE);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, status, JSON.stringify(options)],
  );

  return {
    email,
    setEmail,
    status,
    message,
    submit,
    loading: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
  };
}